import './user-policy-modal.scss';

import useAuth from '@src/core/auth/components/auth0-wrapper';
import { useTranslation } from 'react-i18next';
import { createContext, Context, useState, useContext, useCallback, useEffect, FC } from 'react';
import { useQuery } from 'react-query';
import NiceModal, { NiceModalHandler } from '@ebay/nice-modal-react';
import { Button } from '@progress/kendo-react-buttons';
import {
  CypressTestIdKeysEnum,
  key,
  PageLoadingComponent,
  BasicSimpleModalControllerComponent,
  BasicModalFooterLayoutComponent,
} from 'swo-ciam-common-react';

import { LocaleTranslationKeysEnum } from '@src/core/i18n/locale-translation-keys.enum';
import { useInjectableServices } from '@src/server/service-registry.provider';
import { ApplicationConfigurationService } from '@src/core/application-configuration';

export interface UserPolicyContextValueInterface {
  isAccepted: boolean;
  acceptedVersion: string;
  globalVersion: string;
  user: string;
}
export const UserPolicyContext: Context<UserPolicyContextValueInterface> = createContext(null);

export const UserPolicyProvider: FC = ({ children }) => {
  const [isAcceptingUserPolicy, setIsAcceptingUserPolicy] = useState<boolean>(false);

  const { UserPolicyService } = useInjectableServices();
  const { t: translateText } = useTranslation();
  const { logout } = useAuth();

  const {
    data: userPolicy = { accepted: false, acceptedVersion: '', globalVersion: '', user: '' },
    isLoading: isUserPolicyLoading,
    isSuccess: isUserPolicyReceived,
    refetch: refetchUserPolicy,
  } = useQuery(['get-user-policy'], () => UserPolicyService.getUserPolicy());

  const confirmUserPolicy = useCallback(
    async (event, modal: NiceModalHandler) => {
      setIsAcceptingUserPolicy(true);
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await UserPolicyService.createUserPolicy(userTimezone);
      await refetchUserPolicy();
      modal.remove();
      setIsAcceptingUserPolicy(false);
    },
    [UserPolicyService, refetchUserPolicy]
  );

  const rejectUserPolicy = useCallback(
    (event, modal: NiceModalHandler) => {
      modal.remove();
      logout({ returnTo: window.location.origin });
    },
    [logout]
  );

  const showUserPolicyAgreement = useCallback(async () => {
    NiceModal.show(BasicSimpleModalControllerComponent, {
      title: translateText(LocaleTranslationKeysEnum.SwoUserPolicyTitle),
      nonEscapableMode: true,
      closableByCancelAndX: true,
      width: '480px',
      confirm: confirmUserPolicy,
      reject: !isAcceptingUserPolicy ? rejectUserPolicy : undefined,
      children: () => (
        <div
          style={{
            display: 'grid',
            gap: '2rem',
            paddingBottom: '1rem',
          }}
        >
          <span id="user-policy-modal-marker" style={{ display: 'none' }} />
          {translateText(LocaleTranslationKeysEnum.SwoUserPolicySubTitle)}

          <Button
            style={{
              display: 'grid',
              gap: '0.8rem',
              width: 'max-content',
              gridTemplateColumns: 'min-content 1fr',
              direction: 'rtl',
              justifyItems: 'end',
            }}
            themeColor="dark"
            disabled={isAcceptingUserPolicy}
            onClick={() => {
              window.open(
                ApplicationConfigurationService.getConfiguration().TERMS_AND_CONDITION_RESOURCE_URL,
                '_blank',
                'noopener,noreferrer'
              );
            }}
          >
            {translateText(LocaleTranslationKeysEnum.SwoUserPolicyMessage)}
          </Button>
        </div>
      ),
      footer: (modal: NiceModalHandler) => (
        <BasicModalFooterLayoutComponent
          cancelButtonText={translateText(LocaleTranslationKeysEnum.Cancel)}
          modal={modal}
          rejectHandler={rejectUserPolicy}
          leftSideButtons={[
            <Button
              data-testid={key(CypressTestIdKeysEnum.BUTTON, CypressTestIdKeysEnum.ACTION_CANCEL)}
              type="button"
              className="k-primary"
              themeColor="primary"
              fillMode="flat"
              disabled={isAcceptingUserPolicy}
              onClick={() => rejectUserPolicy(undefined, modal)}
            >
              <span style={{ color: 'var(--brand-primary)' }}>
                {translateText(LocaleTranslationKeysEnum.Cancel)}
              </span>
            </Button>,
          ]}
          rightSideButtons={[
            <Button
              data-testid={key(CypressTestIdKeysEnum.BUTTON, CypressTestIdKeysEnum.ACTION_ACCEPT)}
              type="button"
              className="k-primary"
              themeColor="primary"
              fillMode="solid"
              disabled={isAcceptingUserPolicy}
              onClick={() => confirmUserPolicy(undefined, modal)}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  gap: '0.5rem',
                }}
              >
                {isAcceptingUserPolicy && <PageLoadingComponent size="small" />}
                {translateText(LocaleTranslationKeysEnum.Accept)}
              </div>
            </Button>,
          ]}
        />
      ),
    });
  }, [confirmUserPolicy, rejectUserPolicy, translateText, isAcceptingUserPolicy]);

  useEffect(() => {
    if (
      !isUserPolicyLoading &&
      isUserPolicyReceived &&
      (!userPolicy.accepted || userPolicy.acceptedVersion !== userPolicy.globalVersion)
    ) {
      showUserPolicyAgreement();
    }
  }, [showUserPolicyAgreement, isUserPolicyLoading, isUserPolicyReceived, userPolicy]);

  return (
    <UserPolicyContext.Provider
      value={{
        isAccepted: userPolicy.accepted,
        acceptedVersion: userPolicy.acceptedVersion,
        globalVersion: userPolicy.globalVersion,
        user: userPolicy.user,
      }}
    >
      {children}
    </UserPolicyContext.Provider>
  );
};

export const useUserPolicy = () => {
  const userPolicy = useContext(UserPolicyContext);
  return userPolicy;
};
